import { useState, useRef, useEffect, useCallback } from "react";
import { getRobotAnimation } from "../lib/gemini-ai";

export function useFaceAnimation({ videoRef, isCameraOn, isThinking, isAudioPlaying, isListening, lastSpokenTime, apiKey }) {
  const [animationState, setAnimationState] = useState("offline");
  const [eyePosition, setEyePosition] = useState({ x: 0, y: 0 });
  const [faceBoundingBox, setFaceBoundingBox] = useState(null);
  const [isEyeRolling, setIsEyeRolling] = useState(false);

  const faceDetectorRef = useRef(null);
  const canvasRef = useRef(null);
  const animationFrameRef = useRef();
  const detectionIntervalRef = useRef(null);
  const lastBlinkTimeRef = useRef(0);
  const eyeTargetRef = useRef({ x: 0, y: 0 });
  const lastEyeMoveTimeRef = useRef(0);
  const eyeRollStartTimeRef = useRef(null);
  const winkTriggeredRef = useRef(false);
  const eyeRollTriggeredRef = useRef(false);

  useEffect(() => {
    if (!canvasRef.current) {
      canvasRef.current = document.createElement("canvas");
    }

    if ("FaceDetector" in window && !faceDetectorRef.current) {
      try {
        faceDetectorRef.current = new window.FaceDetector({ fastMode: true });
      } catch (e) {
        console.warn("FaceDetector init failed:", e);
      }
    }
  }, []);

  const triggerEyeRoll = useCallback(() => {
    if (isEyeRolling) return;
    setAnimationState("angry");
    setIsEyeRolling(true);
    eyeRollStartTimeRef.current = performance.now();
    eyeRollTriggeredRef.current = true;
  }, [isEyeRolling]);

  const triggerWink = useCallback(() => {
    const prevState = animationState;
    setAnimationState("wink");
    setTimeout(() => {
      if (animationState === "wink") {
        setAnimationState(prevState === "wink" ? "neutral" : prevState);
      }
    }, 500);
    winkTriggeredRef.current = true;
  }, [animationState]);

  useEffect(() => {
    winkTriggeredRef.current = false;
    eyeRollTriggeredRef.current = false;
  }, [lastSpokenTime]);

  useEffect(() => {
    if (!isCameraOn) {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      if (detectionIntervalRef.current) clearInterval(detectionIntervalRef.current);
      setFaceBoundingBox(null);
      return;
    }

    const video = videoRef.current;
    if (!video) return;

    detectionIntervalRef.current = setInterval(async () => {
      if (!video || !canvasRef.current || isThinking || isAudioPlaying) return;

      const canvas = canvasRef.current;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const context = canvas.getContext("2d");
      if (!context) return;

      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      let faceDetected = false;
      if (faceDetectorRef.current) {
        try {
          const faces = await faceDetectorRef.current.detect(canvas);
          if (faces.length > 0) {
            faceDetected = true;
            setFaceBoundingBox(faces[0].boundingBox);
          } else {
            setFaceBoundingBox(null);
          }
        } catch {
          setFaceBoundingBox(null);
        }
      } else {
        faceDetected = true;
      }

      if (faceDetected && apiKey) {
        const photoDataUri = canvas.toDataURL("image/jpeg");
        try {
          const result = await getRobotAnimation({ photoDataUri, apiKey });
          if (result && result.animation) {
            if (!isAudioPlaying && !isEyeRolling && animationState !== "wink") {
              setAnimationState(result.animation);
            }
          }
        } catch (error) {
          console.error("Expression detection failed:", error);
        }
      }
    }, 2500);

    const animate = () => {
      animationFrameRef.current = requestAnimationFrame(animate);

      if (isAudioPlaying || isThinking || !isListening) return;

      const currentTime = performance.now();
      const silenceDuration = currentTime - lastSpokenTime;

      if (silenceDuration > 60000 && !eyeRollTriggeredRef.current) {
        triggerEyeRoll();
      } else if (silenceDuration > 30000 && !winkTriggeredRef.current) {
        triggerWink();
      }

      if (isEyeRolling) {
        const rollDuration = 1500;
        const timeSinceRollStart = currentTime - (eyeRollStartTimeRef.current || 0);
        if (timeSinceRollStart < rollDuration) {
          const angle = (timeSinceRollStart / rollDuration) * Math.PI * 2;
          eyeTargetRef.current = {
            x: Math.cos(angle) * 1.5,
            y: Math.sin(angle) * 0.8,
          };
        } else {
          setIsEyeRolling(false);
          eyeRollStartTimeRef.current = null;
          setAnimationState("neutral");
          eyeTargetRef.current = { x: 0, y: 0 };
        }
      } else {
        if (currentTime - lastEyeMoveTimeRef.current > 2000 + Math.random() * 3000) {
          eyeTargetRef.current =
            Math.random() > 0.3
              ? { x: 0, y: 0 }
              : { x: (Math.random() - 0.5) * 1.5, y: (Math.random() - 0.5) * 0.5 };
          lastEyeMoveTimeRef.current = currentTime;
        }
      }

      setEyePosition((prev) => ({
        x: prev.x + (eyeTargetRef.current.x - prev.x) * 0.1,
        y: prev.y + (eyeTargetRef.current.y - prev.y) * 0.1,
      }));

      if (
        currentTime - lastBlinkTimeRef.current > 3000 + Math.random() * 6000 &&
        animationState !== "blink" &&
        animationState !== "wink" &&
        animationState !== "offline" &&
        !isEyeRolling &&
        !isAudioPlaying
      ) {
        const prevState = animationState;
        lastBlinkTimeRef.current = currentTime;
        setAnimationState("blink");
        setTimeout(() => setAnimationState(prevState === "blink" ? "neutral" : prevState), 200);
      }
    };

    animate();

    return () => {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
      if (detectionIntervalRef.current) clearInterval(detectionIntervalRef.current);
    };
  }, [isCameraOn, videoRef, isThinking, isAudioPlaying, isListening, lastSpokenTime, animationState, isEyeRolling, triggerWink, triggerEyeRoll, apiKey]);

  return { animationState, setAnimationState, eyePosition, setEyePosition, faceBoundingBox, isEyeRolling, triggerEyeRoll, triggerWink };
}
